import { Core, WebViewerInstance } from '@pdftron/webviewer';
import React, { FC, useCallback } from 'react';
import { AttachmentsAPI } from 'api/attachments';
import { Discussion as Discussion$ } from 'api/document/types/document';
import { useGetDiscussionsForAttachment } from 'queries/attachments';
import { Discussion } from './discussions';

interface Props {
  attachmentId: number;
  projectId: number;
  instance: WebViewerInstance | null;
  discussion: Discussion$;
  activeDiscussion: Discussion$ | null;
  setActiveDiscussion: (discussion: Discussion$ | null) => void;
  setActiveAnnotation?: (
    discussion: Core.Annotations.Annotation | null,
  ) => void;
  setFloatingFormCoordinates?: (body: { x: number; y: number }) => void;
  onDiscussionClick?: () => void;
  showResolvedComments: boolean;
}

export const AttachmentDiscussion: FC<Props> = ({
  attachmentId,
  projectId,
  discussion,
  instance,
  activeDiscussion,
  setActiveDiscussion,
  setFloatingFormCoordinates,
  setActiveAnnotation,
  onDiscussionClick: _onDiscussionClick,
  showResolvedComments,
}) => {
  const { refetch } = useGetDiscussionsForAttachment(attachmentId);

  const onDiscussionClick = useCallback(() => {
    if (_onDiscussionClick) {
      _onDiscussionClick();
    } else {
      instance?.Core.annotationManager.deselectAllAnnotations();
      if (setFloatingFormCoordinates) {
        setFloatingFormCoordinates({ x: 0, y: 0 });
      }
      setActiveDiscussion(discussion);
      const guid = discussion.comments[0]?.commentGuid;

      if (guid) {
        const ann = instance?.Core.annotationManager.getAnnotationById(guid);

        if (ann) {
          instance?.Core.annotationManager.selectAnnotation(ann);
        }
      }
    }
  }, [
    _onDiscussionClick,
    discussion,
    instance?.Core.annotationManager,
    setActiveDiscussion,
    setFloatingFormCoordinates,
  ]);

  const onReply = useCallback(
    ({ content, isPrivate: _isPrivate }) => {
      if (instance) {
        AttachmentsAPI.addReply(
          attachmentId,
          discussion.commentThread.threadId,
          {
            content,
            isPrivate: _isPrivate ?? discussion.comments.at(0)?.private,
          },
        ).then(() => {
          refetch();
        });
      }
    },
    [
      attachmentId,
      discussion.commentThread.threadId,
      discussion.comments,
      instance,
      refetch,
    ],
  );

  const onResolve = useCallback(() => {
    if (setActiveAnnotation) {
      setActiveAnnotation(null);
    }
    if (setFloatingFormCoordinates) {
      setFloatingFormCoordinates({ x: 0, y: 0 });
    }
    const commentGuid = discussion.comments[0]?.commentGuid;
    const selectedAnnotation = commentGuid
      ? instance?.Core.annotationManager.getAnnotationById(commentGuid)
      : null;

    if (selectedAnnotation) {
      instance?.Core.annotationManager.deselectAllAnnotations();
      selectedAnnotation.setCustomData('resolved', 'true');
      if (!showResolvedComments) {
        selectedAnnotation.Hidden = true;
        instance?.Core.annotationManager.updateAnnotation(selectedAnnotation);
      }
    }
  }, [
    discussion.comments,
    instance?.Core.annotationManager,
    setActiveAnnotation,
    setFloatingFormCoordinates,
    showResolvedComments,
  ]);

  const onDeleteThread = useCallback(() => {
    const [comment] = discussion.comments;

    if (setActiveAnnotation) {
      setActiveAnnotation(null);
    }
    if (instance && comment?.commentGuid) {
      instance.Core.annotationManager.deleteAnnotation(
        instance.Core.annotationManager.getAnnotationById(comment.commentGuid),
      );
    }
  }, [discussion.comments, instance, setActiveAnnotation]);

  const onEditComment = useCallback(
    (comment, content) => {
      AttachmentsAPI.updateComment(attachmentId, comment.commentId, {
        content,
      }).then(() => refetch());
    },
    [attachmentId, refetch],
  );

  return (
    <Discussion
      attachmentId={attachmentId}
      discussion={discussion}
      editorCommentMap={{}}
      projectId={projectId}
      isActiveDiscussion={activeDiscussion === discussion}
      onDiscussionClick={onDiscussionClick}
      onReply={onReply}
      onResolve={onResolve}
      onDeleteThread={onDeleteThread}
      onEditComment={onEditComment}
    />
  );
};
